import { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItem,
  Typography,
  Button,
  Divider,
  Box,
  Stack,
  Chip,
  Checkbox,
  Alert,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  CircularProgress,
  Switch,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import formatDate from "../utilities/formatDate";
import request from "../utilities/requests";
import { toast } from "../utilities/SnackBarUtils";

const channelLogos = {
  tnfm: "https://d19w6ranc5yet4.cloudfront.net/tnfm/tnfm-logo.png",
  eventbrite: "https://d19w6ranc5yet4.cloudfront.net/tnfm/eb-logo.png",
};

const UserOrders = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(false);

  const getOrders = async () => {
    try {
      setLoading(true);
      const result = await request(
        `ticketing/admin/orders/?userId=${user._id}`
      );
      if (result.success) {
        setOrders(result.data);
      } else {
        setOrders([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && expanded) {
      getOrders();
    }
  }, [user, expanded]);

  const getAmountStr = (iid) =>
    overrides[iid]?.amountStr ??
    (typeof overrides[iid]?.amountCents === "number"
      ? (overrides[iid].amountCents / 100).toFixed(2)
      : "");
  const getPercentStr = (iid) =>
    overrides[iid]?.percentStr ??
    (typeof overrides[iid]?.percent === "number"
      ? String(overrides[iid].percent)
      : "");

  return (
    <Grid item xs={12}>
      <Paper height={3}>
        <Accordion
          expanded={expanded}
          onChange={(_, isExpanded) => {
            setExpanded(isExpanded);
            if (isExpanded && (!orders || orders.length === 0)) {
              getOrders();
            }
          }}
          slotProps={{ transition: { unmountOnExit: true } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <ListItemButton style={{ fontWeight: "bold" }}>
              Orders
            </ListItemButton>
          </AccordionSummary>

          <AccordionDetails>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loading &&
              (() => {
                return (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {(!orders || orders.length === 0) && (
                      <Alert severity="info">
                        No orders found for this user.
                      </Alert>
                    )}

                    {orders.map((order) => {
                      const orderId = order.orderId || order._id;
                      const items = Array.isArray(order.items)
                        ? order.items
                        : [];

                      const maxRefundable = (it) =>
                        Number(it.refundableAmountCents ?? it.amountCents ?? 0);

                      const getOverride = (iid) => {
                        const ov = overrides[iid] || { mode: "full" };
                        // If user hasn’t touched the toggle yet, default by mode
                        if (
                          typeof ov.removeFromEvent === "undefined" &&
                          !ov.touchedRemove
                        ) {
                          ov.removeFromEvent = ov.mode === "full";
                        }
                        return ov;
                      };

                      const computeItemRefund = (it) => {
                        const iid = String(it.itemId || it._id);
                        const ov = getOverride(iid);
                        const maxCents = maxRefundable(it);
                        let cents = 0;
                        let valid = true;
                        if (ov.mode === "full") {
                          cents = maxCents;
                        } else if (ov.mode === "amount") {
                          const v = ov.amountCents;
                          if (typeof v !== "number") {
                            valid = false;
                            cents = 0;
                          } else {
                            if (!Number.isFinite(v) || v <= 0 || v > maxCents)
                              valid = false;
                            cents = Math.min(
                              Math.max(0, Math.floor(v || 0)),
                              maxCents
                            );
                          }
                        } else if (ov.mode === "percent") {
                          const p = ov.percent;
                          if (typeof p !== "number") {
                            valid = false;
                            cents = 0;
                          } else {
                            if (!Number.isFinite(p) || p <= 0 || p > 100)
                              valid = false;
                            cents = Math.floor(
                              maxCents *
                                (Math.min(Math.max(0, p || 0), 100) / 100)
                            );
                            if (cents <= 0) valid = false;
                          }
                        } else {
                          valid = false;
                        }
                        return { cents, valid, maxCents, ov };
                      };

                      const selectedIds = Object.keys(selected[orderId] || {});
                      const selectedCount = selectedIds.length;

                      // Recompute selected total using overrides
                      const sumSelected = selectedIds.reduce((sum, id) => {
                        const it = items.find(
                          (x) => String(x.itemId || x._id) === String(id)
                        );
                        if (!it) return sum;
                        const { cents, valid } = computeItemRefund(it);
                        return sum + (valid ? cents : 0);
                      }, 0);

                      const hasInvalid = selectedIds.some((id) => {
                        const it = items.find(
                          (x) => String(x.itemId || x._id) === String(id)
                        );
                        if (!it) return true;
                        return computeItemRefund(it).valid === false;
                      });

                      const toggle = (orderId, item) => {
                        if (!item.refundable) return; // guard
                        const iid = String(item.itemId || item._id);
                        setSelected((prev) => {
                          const next = { ...prev };
                          const oid = String(orderId);
                          const map = { ...(next[oid] || {}) };
                          if (map[iid]) delete map[iid];
                          else map[iid] = true;
                          next[oid] = map;
                          return next;
                        });
                        setOverrides((prev) => {
                          const next = { ...prev };
                          if (next[iid]) {
                            // keep existing override when selecting again
                            return next;
                          } else {
                            return { ...next, [iid]: { mode: "full" } };
                          }
                        });
                      };

                      const currency = (order.currency || "usd").toLowerCase();
                      const fmt = (cents = 0) =>
                        new Intl.NumberFormat(undefined, {
                          style: "currency",
                          currency:
                            currency === "usd" ? "USD" : currency.toUpperCase(),
                        }).format((cents || 0) / 100);

                      const handleRefundOrder = async (order) => {
                        const itemIds = Object.keys(selected[orderId] || {});
                        if (itemIds.length === 0) return;
                        try {
                          const itemsPayload = selectedIds.map((id) => {
                            const it = items.find(
                              (x) => String(x.itemId || x._id) === String(id)
                            );
                            const { ov } = computeItemRefund(it);
                            const payload = { itemId: id, mode: ov.mode };
                            if (ov.mode === "amount")
                              payload.amountCents = Math.floor(
                                Number(ov.amountCents) || 0
                              );
                            if (ov.mode === "percent")
                              payload.percent = Math.floor(
                                Number(ov.percent) || 0
                              );
                            payload.removeFromEvent =
                              typeof ov.removeFromEvent === "boolean"
                                ? ov.removeFromEvent
                                : ov.mode === "full";
                            return payload;
                          });
                          const body = {
                            orderId: orderId,
                            selections: itemsPayload,
                            userId: user._id,
                          };
                          const result = await request(
                            "ticketing/admin/refund",
                            "post",
                            body
                          );
                          if (result.success) {
                            toast.success("Refund requested");
                            // Refresh orders
                            getOrders();
                            // Clear selected for this order
                            setSelected((prev) => ({ ...prev, [orderId]: {} }));
                          } else {
                            toast.error(result.error || "Refund failed");
                          }
                        } catch (e) {
                          toast.error(e.message || "Refund failed");
                        }
                      };

                      return (
                        <Paper
                          key={orderId}
                          elevation={0}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: 12,
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ mb: 1 }}
                          >
                            <Stack>
                              <Stack direction="row" alignItems="center">
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={700}
                                >
                                  Order #{order.customerOrderId || orderId}
                                </Typography>
                                <Box
                                  component="img"
                                  src={channelLogos[order.source]}
                                  alt={`${order.source} logo`}
                                  sx={{
                                    height: 16,
                                    width: "auto",
                                    objectFit: "contain",
                                    display: "block",
                                    maxWidth: "100%",
                                    ml: "10px",
                                  }}
                                />
                              </Stack>
                              <Typography variant="subtitle1">
                                {order?.event?.name} |{" "}
                                {formatDate(order?.event?.start)}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Purchased {formatDate(order.paidAt)} • Total{" "}
                                {fmt(order.amountTotalCents)}
                              </Typography>
                            </Stack>
                            {order.source === "tnfm" && (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                {order.refundableWindowOpen ? (
                                  <Chip
                                    size="small"
                                    label={`Refundable items: ${order.refundableCount}`}
                                    color="success"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    label="Outside refund window"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            )}
                          </Stack>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr",
                              gap: 1,
                            }}
                          >
                            {items.map((it) => {
                              // --- Refund-aware display helpers ---
                              const iid = String(it.itemId || it._id);
                              const checked = Boolean(
                                (selected[orderId] || {})[iid]
                              );
                              const disabled = !it.refundable;
                              // Use new server-calculated fields for refund state:
                              const originalItemTotalCents = Number(
                                it.originalTotalCents ?? 0
                              );
                              const remainingRefundableCents = Math.max(
                                0,
                                Number(
                                  it.refundableAmountCents ??
                                    it.amountCents ??
                                    0
                                )
                              );
                              // --- FIX: Only show as refunded if an actual refund happened ---
                              const hasActualRefund =
                                it.refundStatus === "refunded" || Boolean(it.partiallyRefunded);

                              const refundedForItemCents = hasActualRefund
                                ? Math.max(0, originalItemTotalCents - remainingRefundableCents)
                                : 0;

                              const isFullyRefunded =
                                hasActualRefund && remainingRefundableCents === 0;
                              const isPartiallyRefunded =
                                Boolean(it.partiallyRefunded) &&
                                remainingRefundableCents > 0;

                              // Disable selection if fully refunded
                              const selectionDisabled =
                                disabled || isFullyRefunded;
                              return (
                                <Box
                                  key={iid}
                                  sx={{
                                    border: "1px solid",
                                    borderColor: checked
                                      ? "primary.main"
                                      : "divider",
                                    bgcolor: selectionDisabled
                                      ? "action.disabledBackground"
                                      : checked
                                      ? "action.selected"
                                      : "background.paper",
                                    borderRadius: 1.5,
                                    p: 1.25,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5,
                                  }}
                                >
                                  {/* Header row */}
                                  <Grid
                                    container
                                    spacing={1.5}
                                    alignItems="flex-start"
                                  >
                                    {/* Checkbox */}
                                    <Grid item sx={{ pt: 0.25 }}>
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={() => toggle(orderId, it)}
                                        disabled={selectionDisabled}
                                      />
                                    </Grid>

                                    {/* Ticket info */}
                                    <Grid item xs>
                                      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          fontWeight={600}
                                          sx={{ wordBreak: "break-word" }}
                                        >
                                          {it.ticketTypeName}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ wordBreak: "break-word" }}
                                        >
                                          {it.assigneeName
                                            ? `Assigned to: ${it.assigneeName}`
                                            : it.claimStatus === "unassigned"
                                            ? "Unassigned"
                                            : it.assigneeEmail ||
                                              it.assigneeType ||
                                              ""}
                                        </Typography>
                                      </Stack>
                                    </Grid>

                                    {/* Amount chips */}
                                    <Grid item xs={12} sm="auto">
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        justifyContent={{
                                          xs: "flex-start",
                                          sm: "flex-end",
                                        }}
                                        sx={{ flexWrap: "wrap" }}
                                      >
                                        {/* Rule 1: No refunds yet */}
                                        {refundedForItemCents === 0 && (
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            label={`Original: ${fmt(
                                              originalItemTotalCents
                                            )}`}
                                          />
                                        )}

                                        {/* Rule 2: Fully refunded */}
                                        {isFullyRefunded && (
                                          <>
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label={`Original: ${fmt(
                                                originalItemTotalCents
                                              )}`}
                                            />
                                            <Chip
                                              size="small"
                                              color="success"
                                              variant="outlined"
                                              label={`Refunded: ${fmt(
                                                refundedForItemCents
                                              )}`}
                                            />
                                          </>
                                        )}

                                        {/* Rule 3: Partially refunded with balance remaining */}
                                        {isPartiallyRefunded &&
                                          remainingRefundableCents > 0 && (
                                            <>
                                              <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`Original: ${fmt(
                                                  originalItemTotalCents
                                                )}`}
                                              />
                                              <Chip
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                                label={`Remaining: ${fmt(
                                                  remainingRefundableCents
                                                )}`}
                                              />
                                            </>
                                          )}
                                      </Stack>
                                    </Grid>
                                  </Grid>
                                  {checked && (
                                    <Box
                                      sx={{
                                        mt: 1,
                                        width: "100%",
                                        borderTop: "1px dashed",
                                        borderColor: "divider",
                                        pt: 1,
                                      }}
                                    >
                                      <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={2}
                                        alignItems={{
                                          xs: "stretch",
                                          sm: "flex-start",
                                        }}
                                      >
                                        <FormControl
                                          component="fieldset"
                                          size="small"
                                        >
                                          <FormLabel
                                            component="legend"
                                            sx={{
                                              typography: "caption",
                                              mb: 0.5,
                                            }}
                                          >
                                            Refund mode
                                          </FormLabel>
                                          <RadioGroup
                                            row
                                            value={
                                              getOverride(iid).mode || "full"
                                            }
                                            onChange={(e) => {
                                              const mode = e.target.value;
                                              setOverrides((prev) => {
                                                const cur = prev[iid] || {};
                                                const touched = Boolean(
                                                  cur.touchedRemove
                                                );
                                                const next = {
                                                  ...cur,
                                                  mode,
                                                  amountStr: cur.amountStr,
                                                  percentStr: cur.percentStr,
                                                };
                                                if (!touched) {
                                                  // Defaults: full => remove; amount/percent => keep
                                                  next.removeFromEvent =
                                                    mode === "full";
                                                }
                                                return { ...prev, [iid]: next };
                                              });
                                            }}
                                          >
                                            <FormControlLabel
                                              value="full"
                                              control={<Radio size="small" />}
                                              label="Full"
                                            />
                                            <FormControlLabel
                                              value="amount"
                                              control={<Radio size="small" />}
                                              label="Amount"
                                            />
                                            <FormControlLabel
                                              value="percent"
                                              control={<Radio size="small" />}
                                              label="Percent"
                                            />
                                          </RadioGroup>
                                        </FormControl>

                                        {getOverride(iid).mode === "amount" && (
                                          <FormControl
                                            size="small"
                                            sx={{ minWidth: 180 }}
                                          >
                                            <FormLabel
                                              sx={{
                                                typography: "caption",
                                                mb: 0.5,
                                              }}
                                            >
                                              Amount (max{" "}
                                              {fmt(maxRefundable(it))})
                                            </FormLabel>
                                            <TextField
                                              size="small"
                                              placeholder="0.00"
                                              value={getAmountStr(iid)}
                                              onChange={(e) => {
                                                const raw = e.target.value;
                                                // Allow only digits and a single dot, keep as raw string for UX
                                                const sanitized = raw
                                                  .replace(/[^0-9.]/g, "")
                                                  .replace(/(\..*)\./g, "$1");
                                                // Compute cents but do not format back into the field
                                                const cents =
                                                  sanitized === ""
                                                    ? undefined
                                                    : Math.round(
                                                        (Number.parseFloat(
                                                          sanitized
                                                        ) || 0) * 100
                                                      );
                                                setOverrides((prev) => ({
                                                  ...prev,
                                                  [iid]: {
                                                    ...(prev[iid] || {
                                                      mode: "amount",
                                                    }),
                                                    mode: "amount",
                                                    amountStr: sanitized,
                                                    amountCents:
                                                      typeof cents === "number"
                                                        ? cents
                                                        : undefined,
                                                  },
                                                }));
                                              }}
                                              onBlur={() => {
                                                // On blur, normalize the display to 2 decimals if there is a number
                                                setOverrides((prev) => {
                                                  const cur = prev[iid];
                                                  if (!cur) return prev;
                                                  if (
                                                    typeof cur.amountCents !==
                                                    "number"
                                                  )
                                                    return prev;
                                                  return {
                                                    ...prev,
                                                    [iid]: {
                                                      ...cur,
                                                      amountStr: (
                                                        cur.amountCents / 100
                                                      ).toFixed(2),
                                                    },
                                                  };
                                                });
                                              }}
                                              inputProps={{
                                                inputMode: "decimal",
                                              }}
                                            />
                                            {!computeItemRefund(it).valid && (
                                              <FormHelperText error>
                                                Enter an amount between $0.01
                                                and {fmt(maxRefundable(it))}
                                              </FormHelperText>
                                            )}
                                          </FormControl>
                                        )}

                                        {getOverride(iid).mode ===
                                          "percent" && (
                                          <FormControl
                                            size="small"
                                            sx={{ minWidth: 160 }}
                                          >
                                            <FormLabel
                                              sx={{
                                                typography: "caption",
                                                mb: 0.5,
                                              }}
                                            >
                                              Percent
                                            </FormLabel>
                                            <TextField
                                              size="small"
                                              placeholder="0-100"
                                              value={getPercentStr(iid)}
                                              onChange={(e) => {
                                                const raw = e.target.value;
                                                const digits = raw.replace(
                                                  /[^0-9]/g,
                                                  ""
                                                );
                                                // Keep raw digits while typing
                                                let p =
                                                  digits === ""
                                                    ? undefined
                                                    : Math.min(
                                                        100,
                                                        Math.max(
                                                          0,
                                                          parseInt(digits, 10)
                                                        )
                                                      );
                                                setOverrides((prev) => ({
                                                  ...prev,
                                                  [iid]: {
                                                    ...(prev[iid] || {
                                                      mode: "percent",
                                                    }),
                                                    mode: "percent",
                                                    percentStr: digits,
                                                    percent:
                                                      typeof p === "number"
                                                        ? p
                                                        : undefined,
                                                  },
                                                }));
                                              }}
                                              onBlur={() => {
                                                // Normalize to integer 0-100 on blur
                                                setOverrides((prev) => {
                                                  const cur = prev[iid];
                                                  if (!cur) return prev;
                                                  if (
                                                    typeof cur.percent !==
                                                    "number"
                                                  )
                                                    return prev;
                                                  return {
                                                    ...prev,
                                                    [iid]: {
                                                      ...cur,
                                                      percentStr: String(
                                                        cur.percent
                                                      ),
                                                    },
                                                  };
                                                });
                                              }}
                                              inputProps={{
                                                inputMode: "numeric",
                                                min: 0,
                                                max: 100,
                                              }}
                                            />
                                            {!computeItemRefund(it).valid && (
                                              <FormHelperText error>
                                                Enter a percentage between 1 and
                                                100
                                              </FormHelperText>
                                            )}
                                          </FormControl>
                                        )}
                                        {it?.claimStatus === "claimed" ? (
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                size="small"
                                                checked={Boolean(
                                                  getOverride(iid)
                                                    .removeFromEvent
                                                )}
                                                onChange={(e) => {
                                                  const checked =
                                                    e.target.checked;
                                                  setOverrides((prev) => ({
                                                    ...prev,
                                                    [iid]: {
                                                      ...(prev[iid] || {}),
                                                      removeFromEvent: checked,
                                                      touchedRemove: true, // remember user explicitly chose
                                                    },
                                                  }));
                                                }}
                                              />
                                            }
                                            label={
                                              <Stack spacing={0}>
                                                <Typography variant="caption">
                                                  Remove from event
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  Attendee will be removed
                                                  immediately
                                                </Typography>
                                              </Stack>
                                            }
                                            sx={{ ml: 1 }}
                                          />
                                        ) : null}

                                        <Box sx={{ ml: "auto" }}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Will refund:&nbsp;
                                            <strong>
                                              {fmt(computeItemRefund(it).cents)}
                                            </strong>
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>

                          <Divider sx={{ my: 1.25 }} />
                          {order.source === "tnfm" && (
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              alignItems={{ xs: "stretch", sm: "center" }}
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {selectedCount > 0
                                  ? `${selectedCount} selected`
                                  : "Select refundable tickets to process a refund"}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Chip
                                  size="small"
                                  label={`Selected total: ${fmt(sumSelected)}`}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={selectedCount === 0 || hasInvalid}
                                  onClick={() => handleRefundOrder(order)}
                                >
                                  Process refund
                                </Button>
                              </Stack>
                            </Stack>
                          )}
                        </Paper>
                      );
                    })}
                  </div>
                );
              })()}
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Grid>
  );
};

export default UserOrders;
